import {captureSentryMessage} from 'src/utils/sentry';

import type {Row, WidgetConfig} from 'src/types/byod';

/**
 * Per-widget payload entry. Same shape for new and existing widgets —
 * the backend distinguishes them by checking whether the placement id
 * already exists on this dashboard in the DB.
 *
 * `widget_key` is always sent. For existing placements it must match
 * what's in the DB; the backend should reject changes (renames are not
 * a real case — to "rename" a widget the user deletes + adds, which
 * surfaces as a new UUID + a missing UUID).
 */
export type WidgetInput = {
  widget_key: string;
  widget_filters: Record<string, unknown>;
  widget_config: Record<string, unknown>;
};

export type SaveAndPublishRequest = {
  name: string;
  description: string;
  /** Filter keys the dashboard exposes as global filters (empty until a UI sets them). */
  global_filters: string[];
  /** Final layout. Every tile id here must appear as a key in `widgets`. */
  layouts: Row[];
  /**
   * Full final widget set keyed by placement id (UUID minted by the
   * frontend at widget creation). The backend computes the diff:
   *   - Key not in DB on this dashboard → new placement, insert as-is
   *     using the UUID from the payload.
   *   - Key in DB and content matches → no-op.
   *   - Key in DB and content differs → update.
   *   - Key in DB but missing from this map → remove in the same
   *     transaction.
   */
  widgets: Record<string, WidgetInput>;
};

type StoreSlice = {
  name: string;
  description: string;
  rowOrder: string[];
  rows: Record<string, Row>;
  widgets: Record<string, WidgetConfig>;
  globalFilters?: string[];
};

function toWidgetInput(w: WidgetConfig): WidgetInput {
  return {
    widget_key: w.widget_key,
    widget_filters: w.widget_filters_json,
    widget_config: {
      ...w.widget_config_json,
      label: w.label,
      widget_icon: w.widget_icon,
      widget_type: w.widget_type,
    },
  };
}

/**
 * Build the save-and-publish request body from current store state.
 *
 * The frontend doesn't diff and doesn't carry a "client id" prefix
 * convention — every placement id is a frontend-minted UUID that the
 * backend stores verbatim. The backend works out new / removed /
 * modified by comparing the payload against the current DB record.
 */
export function buildSaveAndPublishPayload(
  state: StoreSlice,
): SaveAndPublishRequest {
  const widgets: Record<string, WidgetInput> = {};
  for (const [id, w] of Object.entries(state.widgets)) {
    widgets[id] = toWidgetInput(w);
  }

  // Strip orphaned tile ids — a layout entry referencing a missing widget
  // would tell the backend "place this tile, but the widget isn't here",
  // which the diff treats as a removal. The result: the tile silently
  // disappears next render. Filter defensively and report so we can find
  // the upstream bug (likely a missed cleanup when a widget is deleted).
  const orphanedTiles: Array<{rowId: string; tileId: string}> = [];
  const layouts: Row[] = state.rowOrder
    .map((id) => state.rows[id])
    .filter((row): row is Row => Boolean(row))
    .map((row) => {
      const cleanTiles = row.tiles.filter((tileId) => {
        if (widgets[tileId]) {
          return true;
        }
        orphanedTiles.push({rowId: row.id, tileId});
        return false;
      });
      return cleanTiles.length === row.tiles.length
        ? row
        : {...row, tiles: cleanTiles};
    });

  if (orphanedTiles.length > 0) {
    captureSentryMessage(
      'BYOD save payload contained orphaned tile ids — filtered before send',
      {
        level: 'warning',
        tags: {feature: 'byod', context: 'save-payload'},
        extra: {orphanedTiles, dashboardName: state.name},
      },
    );
  }

  return {
    name: state.name,
    description: state.description,
    global_filters: state.globalFilters ?? [],
    layouts,
    widgets,
  };
}
